import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { ChevronDown, ChevronUp, Edit2, Loader, Save, Search, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import { adminServices } from "../services/AdminServices";

if (typeof window !== "undefined" && document.getElementById("root")) {
  Modal.setAppElement("#root");
}

const EMPTY_FORM = {
  name: "",
  status: "Active"
};

const normalizeStatus = (value) => {
  const raw = String(value ?? "").trim().toLowerCase();
  if (raw === "active" || raw === "1") return "Active";
  if (raw === "inactive" || raw === "0") return "Inactive";
  return "Active";
};

const toTypeItem = (item, index) => ({
  id: Number(item?.id) || index + 1,
  name: String(item?.name ?? "").trim(),
  status: normalizeStatus(item?.status),
  regDate: item?.regDate || item?.reg_date || null
});

const SupplierTypeManagementModal = ({ isOpen, onClose, onUpdated }) => {
  const [supplierTypes, setSupplierTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    if (isOpen) {
      fetchSupplierTypes();
    }
  }, [isOpen]);

  const fetchSupplierTypes = async () => {
    setLoading(true);
    try {
      const response = await adminServices.storeProcedure2('getSupplierTypes',{});
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
      setSupplierTypes(data.map(toTypeItem));
    } catch (error) {
      console.error("Failed to fetch supplier types:", error);
      toast.error("Failed to load supplier types");
      setSupplierTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const handleClose = () => {
    resetForm();
    setSearchQuery("");
    onClose();
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleSave = async () => {
    const trimmedName = String(form.name || "").trim();
    if (!trimmedName) {
      toast.error("Supplier Type name is required");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const existing = supplierTypes.find((item) => item.id === editingId);
        const payload = {
          supplierTypeId: editingId,
          supplierType: trimmedName,
          typeStatus: form.status,
        };

        await adminServices.storeProcedure2('updateSupplierType', payload);
        toast.success("Supplier Type updated successfully");
      } else {
        const payload = {
          typeName: trimmedName,
          typeStatus: form.status
        };

        await adminServices.storeProcedure2('addSupplierType', payload);
        toast.success("Supplier Type created successfully");
      }

      resetForm();
      await fetchSupplierTypes();
      onUpdated?.();
    } catch (error) {
      console.error("Failed to save supplier type:", error);
      toast.error(error?.response?.data?.message || "Failed to save supplier type");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      status: normalizeStatus(item.status)
    });
  };

  const handleDelete = async (item) => {
    const shouldDelete = window.confirm(`Delete supplier type \"${item.name}\"?`);
    if (!shouldDelete) return;

    setSaving(true);
    try {
      await adminServices.storeProcedure2("removeSupplierType",{"supplierTypeId":item.id});
      toast.success("Supplier Type deleted successfully");
      if (editingId === item.id) {
        resetForm();
      }
      await fetchSupplierTypes();
      onUpdated?.();
    } catch (error) {
      console.error("Failed to delete supplier type:", error);
      toast.error(error?.response?.data?.message || "Failed to delete supplier type");
    } finally {
      setSaving(false);
    }
  };

  const filteredTypes = useMemo(() => {
    const query = String(searchQuery || "").trim().toLowerCase();
    let result = supplierTypes;

    if (query) {
      result = result.filter((item) => {
        return (
          String(item.name || "").toLowerCase().includes(query) ||
          String(item.status || "").toLowerCase().includes(query)
        );
      });
    }

    // Apply sorting
    const sorted = [...result].sort((a, b) => {
      let aVal, bVal;

      if (sortColumn === "id") {
        aVal = a.id;
        bVal = b.id;
      } else if (sortColumn === "name") {
        aVal = String(a.name || "").toLowerCase();
        bVal = String(b.name || "").toLowerCase();
      } else if (sortColumn === "status") {
        aVal = String(a.status || "").toLowerCase();
        bVal = String(b.status || "").toLowerCase();
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [searchQuery, supplierTypes, sortColumn, sortDirection]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="uom-manager-modal"
      overlayClassName="uom-manager-modal-overlay"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={!saving}
    >
      <div className="uom-manager-modal-content">
        <div className="uom-manager-modal-header">
          <h2 className="uom-manager-modal-title">Supplier Type Management</h2>
          <button
            className="uom-manager-modal-close"
            onClick={handleClose}
            disabled={saving}
          >
            <X size={20} />
          </button>
        </div>

        <div className="uom-manager-modal-body">
          <div className="uom-manager-add-section">
            <label className="uom-manager-input-label">Supplier Type</label>
            <div className="uom-manager-input-group" style={{ marginBottom: 8 }}>
              <input
                type="text"
                className="uom-manager-input"
                placeholder="Enter supplier type name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                disabled={saving}
                maxLength={150}
              />
              <select
                className="uom-manager-input"
                value={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                disabled={saving}
                style={{ maxWidth: 180 }}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <button
                className="uom-manager-add-btn"
                onClick={handleSave}
                disabled={saving || !String(form.name || "").trim()}
              >
                {saving ? <Loader size={16} className="spinner" /> : editingId ? <Save size={16} /> : "+"}
                {editingId ? "Save" : "Add Supplier Type"}
              </button>
              {editingId ? (
                <button
                  className="supplier-import-btn"
                  style={{ backgroundColor: "#ffffff", color: "#111827", border: "1px solid #d1d5db" }}
                  onClick={resetForm}
                  disabled={saving}
                >
                  Cancel
                </button>
              ) : null}
            </div>
          </div>

          <div className="uom-manager-search-container">
            <Search className="uom-manager-search-icon" size={18} />
            <input
              type="text"
              className="uom-manager-search-input"
              placeholder="Search supplier type"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="uom-manager-list-section">
            {loading ? (
              <div className="uom-manager-loading">
                <Loader size={24} className="spinner" />
                <p>Loading supplier types...</p>
              </div>
            ) : filteredTypes.length === 0 ? (
              <div className="uom-manager-empty">
                <p>{supplierTypes.length ? "No matching supplier types found." : "No supplier types found."}</p>
              </div>
            ) : (
              <div className="uom-manager-table">
                <div className="uom-manager-table-header">
                  <button className="uom-manager-table-header-cell id-cell" onClick={() => handleSort("id")}>
                    ID
                    {sortColumn === "id" && (
                      sortDirection === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                    )}
                  </button>
                  <button className="uom-manager-table-header-cell name-cell" onClick={() => handleSort("name")}>
                    Supplier Type
                    {sortColumn === "name" && (
                      sortDirection === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                    )}
                  </button>
                  <button className="uom-manager-table-header-cell name-cell" onClick={() => handleSort("status")}>
                    Status
                    {sortColumn === "status" && (
                      sortDirection === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                    )}
                  </button>
                  <div className="uom-manager-table-cell action-cell">Action</div>
                </div>
                <div className="uom-manager-table-body">
                  {filteredTypes.map((item) => (
                    <div key={item.id} className="uom-manager-table-row">
                      <div className="uom-manager-table-cell id-cell">{item.id}</div>
                      <div className="uom-manager-table-cell name-cell">{item.name || "-"}</div>
                      <div className="uom-manager-table-cell name-cell">{item.status || "-"}</div>
                      <div className="uom-manager-table-cell action-cell" style={{ gap: 8 }}>
                        <button
                          className="uom-manager-delete-btn"
                          onClick={() => handleEdit(item)}
                          disabled={saving}
                          title="Edit"
                          style={{ color: "#1d4ed8" }}
                        >
                          <Edit2 size={17} />
                        </button>
                        <button
                          className="uom-manager-delete-btn"
                          onClick={() => handleDelete(item)}
                          disabled={saving}
                          title="Delete"
                        >
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .uom-manager-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .uom-manager-modal {
          position: relative;
          background: white;
          border-radius: 8px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          max-width: 820px;
          width: 92%;
          max-height: 90vh;
          overflow-y: auto;
          outline: none;
        }

        .uom-manager-modal-content {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .uom-manager-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px;
          border-bottom: 1px solid #e5e7eb;
          flex-shrink: 0;
        }

        .uom-manager-modal-title {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
        }

        .uom-manager-modal-close {
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          color: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s;
        }

        .uom-manager-modal-close:hover:not(:disabled) {
          background-color: #f3f4f6;
          color: #1f2937;
        }

        .uom-manager-modal-close:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .uom-manager-modal-body {
          padding: 24px;
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .uom-manager-add-section {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .uom-manager-input-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 4px;
        }

        .uom-manager-input-group {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .uom-manager-input {
          flex: 1;
          min-width: 160px;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          font-family: inherit;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .uom-manager-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .uom-manager-input:disabled {
          background-color: #f3f4f6;
          cursor: not-allowed;
          opacity: 0.7;
        }

        .uom-manager-add-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          background-color: #002347;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .uom-manager-add-btn:hover:not(:disabled) {
          background-color: #001a34;
        }

        .uom-manager-add-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .uom-manager-search-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .uom-manager-search-icon {
          position: absolute;
          left: 12px;
          color: #9ca3af;
          pointer-events: none;
        }

        .uom-manager-search-input {
          width: 100%;
          padding: 10px 12px 10px 38px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          font-family: inherit;
          transition: border-color 0.2s;
        }

        .uom-manager-search-input:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .uom-manager-list-section {
          flex: 1;
          overflow-y: auto;
          min-height: 200px;
        }

        .uom-manager-loading,
        .uom-manager-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 40px 24px;
          color: #6b7280;
          text-align: center;
        }

        .uom-manager-table {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          overflow: hidden;
        }

        .uom-manager-table-header {
          display: grid;
          grid-template-columns: 80px minmax(160px, 1fr) 140px 110px;
          background-color: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          font-weight: 600;
          font-size: 0.875rem;
          color: #374151;
        }

        .uom-manager-table-header-cell {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 12px;
          text-align: left;
          background: none;
          border: none;
          font-weight: 600;
          font-size: 0.875rem;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
        }

        .uom-manager-table-header-cell:hover {
          background-color: #eff6ff;
          color: #1e40af;
        }

        .uom-manager-table-header-cell.id-cell,
        .uom-manager-table-header-cell.action-cell {
          text-align: center;
          justify-content: center;
        }

        .uom-manager-table-cell {
          padding: 12px;
          text-align: left;
        }

        .uom-manager-table-cell.id-cell,
        .uom-manager-table-cell.action-cell {
          text-align: center;
        }

        .uom-manager-table-body {
          display: flex;
          flex-direction: column;
        }

        .uom-manager-table-row {
          display: grid;
          grid-template-columns: 80px minmax(160px, 1fr) 140px 110px;
          border-bottom: 1px solid #e5e7eb;
          transition: background-color 0.2s;
        }

        .uom-manager-table-row:hover {
          background-color: #f9fafb;
        }

        .uom-manager-table-row:last-child {
          border-bottom: none;
        }

        .uom-manager-table-row .uom-manager-table-cell {
          padding: 12px;
          font-size: 0.875rem;
          color: #1f2937;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .uom-manager-delete-btn {
          background: none;
          border: none;
          padding: 6px;
          cursor: pointer;
          color: #ef4444;
          border-radius: 4px;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .uom-manager-delete-btn:hover:not(:disabled) {
          background-color: #fee2e2;
        }

        .uom-manager-delete-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .uom-manager-modal {
            width: 96%;
          }

          .uom-manager-modal-header,
          .uom-manager-modal-body {
            padding: 16px;
          }

          .uom-manager-table {
            overflow-x: auto;
          }

          .uom-manager-table-header,
          .uom-manager-table-row {
            min-width: 560px;
          }
        }
      `}</style>
    </Modal>
  );
};

export default SupplierTypeManagementModal;
